import type {
  AssistantStreamEvent,
  ChatMessageArtifact,
  ChatMessageResponse,
} from '@laplace/contracts'
import type { ChatMessage } from '@/features/chats/types/chat'
import { ky } from '@/shared/ky/ky-with-auth'

const ASSISTANT_STREAM_IDLE_TIMEOUT_MS = 60_000

interface StreamAssistantMessagePayload {
  workspaceId: string
  chatId: string
  content: string
  agentId?: string
}

interface StreamAssistantMessageCallbacks {
  onStatus: (event: Extract<AssistantStreamEvent, { type: 'status' }>) => void
  // Raw model reasoning. The UI no longer renders this (the thinking panel
  // shows only the high-level action timeline); kept optional so the stream
  // still parses `thinking` events and a debug consumer could opt in.
  onThinking?: (delta: string) => void
  onDelta: (delta: string) => void
  onArtifact?: (artifact: ChatMessageArtifact) => void
  onDone: (message: ChatMessage) => void
  onApprovalRequired?: (message: ChatMessage) => void
  onApprovalAutoApproved?: (message: ChatMessage) => void
}

interface StreamAssistantMessageOptions {
  signal?: AbortSignal
}

export class AssistantStreamClientError extends Error {
  readonly code?: string
  readonly partialMessage?: ChatMessage

  constructor(
    message: string,
    options: { code?: string; partialMessage?: ChatMessage } = {},
  ) {
    super(message)
    this.name = 'AssistantStreamClientError'
    this.code = options.code
    this.partialMessage = options.partialMessage
  }
}

function normalizeAssistantMessage(message: ChatMessageResponse): ChatMessage {
  return {
    ...message,
    role: 'ASSISTANT',
    sourceFreshness: message.sourceFreshness ?? [],
    artifacts: message.artifacts ?? [],
    approvals: message.approvals ?? [],
  }
}

function isAssistantStreamEvent(value: unknown): value is AssistantStreamEvent {
  if (value === null || typeof value !== 'object') {
    return false
  }

  const event = value as { type?: unknown }
  return (
    event.type === 'status' ||
    event.type === 'thinking' ||
    event.type === 'delta' ||
    event.type === 'artifact' ||
    event.type === 'approval_required' ||
    event.type === 'approval_auto_approved' ||
    event.type === 'done' ||
    event.type === 'error' ||
    event.type === 'heartbeat'
  )
}

export async function streamAssistantMessage(
  payload: StreamAssistantMessagePayload,
  callbacks: StreamAssistantMessageCallbacks,
  options: StreamAssistantMessageOptions = {},
): Promise<void> {
  const response = await ky.post(
    `workspaces/${payload.workspaceId}/chats/${payload.chatId}/messages/assistant/stream`,
    {
      json: {
        content: payload.content,
        ...(payload.agentId ? { agentId: payload.agentId } : {}),
      },
      signal: options.signal,
      timeout: false,
      retry: 0,
    },
  )

  if (!response.body) {
    throw new Error('Assistant stream is unavailable.')
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let receivedTerminal = false

  while (true) {
    let timeout: ReturnType<typeof setTimeout> | undefined
    const idleTimeout = new Promise<never>((_, reject) => {
      timeout = setTimeout(() => {
        reject(new Error('Assistant stream heartbeat timed out'))
      }, ASSISTANT_STREAM_IDLE_TIMEOUT_MS)
    })

    let result: ReadableStreamReadResult<Uint8Array>
    try {
      result = await Promise.race([reader.read(), idleTimeout])
    } catch (error) {
      await reader.cancel().catch(() => undefined)
      throw error
    } finally {
      if (timeout !== undefined) {
        clearTimeout(timeout)
      }
    }

    const { done, value } = result
    buffer += decoder.decode(value, { stream: !done })

    const lines = buffer.split('\n')
    buffer = lines.pop() ?? ''

    for (const line of lines) {
      const trimmed = line.trim()
      if (!trimmed) {
        continue
      }

      const parsed = JSON.parse(trimmed) as unknown
      if (!isAssistantStreamEvent(parsed)) {
        continue
      }

      if (parsed.type === 'heartbeat') {
        continue
      }

      if (parsed.type === 'status') {
        callbacks.onStatus(parsed)
        continue
      }

      if (parsed.type === 'thinking') {
        callbacks.onThinking?.(parsed.delta)
        continue
      }

      if (parsed.type === 'delta') {
        callbacks.onDelta(parsed.delta)
        continue
      }

      if (parsed.type === 'artifact') {
        callbacks.onArtifact?.(parsed.artifact)
        continue
      }

      if (parsed.type === 'done') {
        receivedTerminal = true
        callbacks.onDone(normalizeAssistantMessage(parsed.message))
        continue
      }

      if (parsed.type === 'approval_required') {
        receivedTerminal = true
        callbacks.onApprovalRequired?.(
          normalizeAssistantMessage(parsed.message),
        )
        continue
      }

      if (parsed.type === 'approval_auto_approved') {
        receivedTerminal = true
        callbacks.onApprovalAutoApproved?.(
          normalizeAssistantMessage(parsed.message),
        )
        continue
      }

      if (parsed.type === 'error') {
        receivedTerminal = true
        throw new AssistantStreamClientError(
          parsed.error || 'Assistant stream failed.',
          {
            ...(parsed.code ? { code: parsed.code } : {}),
            ...(parsed.message
              ? {
                  partialMessage: normalizeAssistantMessage(parsed.message),
                }
              : {}),
          },
        )
      }
    }

    if (done) {
      break
    }
  }

  if (!receivedTerminal) {
    throw new AssistantStreamClientError(
      'The assistant stream ended without a complete response.',
    )
  }
}
